import React, { useEffect, useRef, useState } from "react";
import { useLocation, Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";

export default function BillingSuccess() {
  const loc = useLocation();
  const nav = useNavigate();
  const { refresh } = useAuth();
  const [status, setStatus] = useState("polling"); // polling | paid | failed | timeout
  const attempts = useRef(0);

  const sessionId = new URLSearchParams(loc.search).get("session_id");

  useEffect(() => {
    if (!sessionId) { setStatus("failed"); return; }
    let alive = true;
    const poll = async () => {
      if (!alive) return;
      attempts.current += 1;
      try {
        const { data } = await api.get(`/billing/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setStatus("paid");
          await refresh();
          return;
        }
        if (data.status === "expired") { setStatus("failed"); return; }
      } catch {}
      if (attempts.current >= 10) { setStatus("timeout"); return; }
      setTimeout(poll, 2000);
    };
    poll();
    return () => { alive = false; };
  }, [sessionId, refresh]);

  return (
    <div className="min-h-[70vh] flex items-center justify-center px-5">
      <div className="nb-card p-10 max-w-lg w-full text-center" data-testid="billing-success">
        {status === "polling" && (<>
          <Loader2 className="w-10 h-10 animate-spin mx-auto" />
          <h1 className="text-2xl font-black mt-4">Confirming your payment…</h1>
          <p className="text-gray-600 mt-2 text-sm">This usually takes a few seconds.</p>
        </>)}
        {status === "paid" && (<>
          <CheckCircle2 className="w-14 h-14 text-[#22c55e] mx-auto" strokeWidth={2.5} />
          <h1 className="text-3xl font-black mt-4">You're Pro! 🎉</h1>
          <p className="text-gray-600 mt-2">30 days of unlimited AI resume tailoring and tracking unlocked.</p>
          <div className="mt-6 flex justify-center gap-2">
            <Link to="/tailor" className="nb-btn bg-[#4338ca] text-white px-5 py-2.5">Start tailoring</Link>
            <Link to="/jobs" className="nb-btn bg-white px-5 py-2.5">Browse jobs</Link>
          </div>
        </>)}
        {(status === "failed" || status === "timeout") && (<>
          <XCircle className="w-14 h-14 text-[#ec4899] mx-auto" />
          <h1 className="text-2xl font-black mt-4">Payment not confirmed</h1>
          <p className="text-gray-600 mt-2 text-sm">If you just paid, refresh in a moment. Otherwise, try again.</p>
          <button onClick={() => nav("/pricing")} className="mt-6 nb-btn bg-[#4338ca] text-white px-5 py-2.5">Back to pricing</button>
        </>)}
      </div>
    </div>
  );
}
